import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { LANGS } from "../i18n/translations";
import { api } from "../lib/apiClient";
import {
  LayoutDashboard, Building2, Router, Gauge, Users, CalendarCheck,
  Activity, Wifi, BarChart3, RefreshCw, LogOut, ChevronDown, Radio, Plug, FileCode2, Terminal, Database,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/structures", key: "structures", icon: Building2, superOnly: true },
  { to: "/routers", key: "routers", icon: Router },
  { to: "/mikrotik-config", key: "mk_config", icon: Terminal },
  { to: "/freeradius", key: "freeradius", icon: Database },
  { to: "/profiles", key: "profiles", icon: Gauge },
  { to: "/users", key: "users", icon: Users },
  { to: "/reservations", key: "reservations", icon: CalendarCheck },
  { to: "/sessions", key: "sessions", icon: Activity },
  { to: "/portal", key: "portal", icon: Wifi },
  { to: "/reports", key: "reports", icon: BarChart3 },
  { to: "/integrations", key: "integrations", icon: Plug },
  { to: "/api-docs", key: "api_docs", icon: FileCode2 },
  { to: "/logs", key: "logs", icon: RefreshCw },
];

export default function Layout({ children }) {
  const { user, logout, isSuperAdmin, structureId, selectStructure } = useAuth();
  const { t, lang, changeLang } = useI18n();
  const [structures, setStructures] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/structures").then((r) => setStructures(r.data)).catch(() => {});
  }, []);

  const currentStruct = structures.find((s) => s.id === (isSuperAdmin ? structureId : user?.structure_id));

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#1F2937] bg-[#0d121c] fixed inset-y-0 z-40">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-[#1F2937]">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Radio className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">GoWeb</div>
            <div className="text-[10px] uppercase tracking-widest text-blue-400">RADIUS Manager</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((n) => !n.superOnly || isSuperAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-600/15 text-blue-300 ring-1 ring-blue-600/30" : "text-slate-400 hover:text-slate-100 hover:bg-white/5"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1F2937]">
          <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" /> {t("common.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-30 flex items-center justify-between gap-3 px-4 sm:px-6 backdrop-blur-xl bg-[#0B0F17]/80 border-b border-[#1F2937]">
          <div className="flex items-center gap-3 min-w-0">
            {/* Structure selector */}
            {isSuperAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="structure-select-dropdown" className="flex items-center gap-2 rounded-lg border border-[#1F2937] bg-[#111827] px-3 py-2 text-sm font-medium hover:border-blue-600/40 transition-colors">
                    <Building2 className="h-4 w-4 text-blue-400" />
                    <span className="truncate max-w-[160px]">{currentStruct ? currentStruct.name : t("common.all_structures")}</span>
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 bg-[#111827] border-[#1F2937] text-slate-100">
                  <DropdownMenuItem data-testid="structure-opt-all" onClick={() => selectStructure("")} className="cursor-pointer focus:bg-white/5">
                    {t("common.all_structures")}
                  </DropdownMenuItem>
                  {structures.map((s) => (
                    <DropdownMenuItem key={s.id} data-testid={`structure-opt-${s.id}`} onClick={() => selectStructure(s.id)} className="cursor-pointer focus:bg-white/5">
                      {s.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-[#1F2937] bg-[#111827] px-3 py-2 text-sm font-medium">
                <Building2 className="h-4 w-4 text-blue-400" />
                <span className="truncate max-w-[180px]">{currentStruct?.name || "—"}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-500/20">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> FreeRADIUS
            </span>
            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="lang-select" className="flex items-center gap-1.5 rounded-lg border border-[#1F2937] bg-[#111827] px-3 py-2 text-sm font-semibold hover:border-blue-600/40 transition-colors">
                  {LANGS.find((l) => l.code === lang)?.flag}
                  <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#111827] border-[#1F2937] text-slate-100">
                {LANGS.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => changeLang(l.code)} className="cursor-pointer focus:bg-white/5">
                    <span className="font-mono text-xs mr-2 text-blue-400">{l.flag}</span> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="h-9 w-9 rounded-full bg-blue-600/20 ring-1 ring-blue-600/30 flex items-center justify-center text-sm font-bold text-blue-300">
              {(user?.name || "?").charAt(0)}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full">{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 fade-up">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
